import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setDone(true);
      toast.success("Contraseña actualizada correctamente");
    } catch (err) {
      const msg = err.response?.data?.detail || "Enlace inválido o expirado";
      // Only treat as a "broken link" when the backend says so.
      // For validation errors (length, repeated password, etc.) we keep the form
      // open so the user can try another password without losing context.
      const lower = String(msg).toLowerCase();
      const isTokenError =
        lower.includes("enlace") ||
        lower.includes("expirado") ||
        lower.includes("expir") ||
        lower.includes("inválido") ||
        lower.includes("invalid") ||
        lower.includes("ya fue usado");
      if (isTokenError) {
        setError(msg);
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Back */}
      <div className="p-4 absolute top-0 left-0 z-50">
        <Button
          variant="ghost"
          onClick={() => navigate("/login")}
          className="gap-2 text-white bg-black/40 hover:bg-black/60 backdrop-blur-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver al login
        </Button>
      </div>

      <div className="flex-1 flex">
        {/* Left panel */}
        <div
          className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1769774506254-4be19bf63a23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxza2lpbmclMjBhY3Rpb24lMjBzbm93JTIwbW91bnRhaW4lMjBwb3dkZXJ8ZW58MHx8fHwxNzcxMDY3ODE4fDA&ixlib=rb-4.1.0&q=85')",
          }}
        >
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative z-10 flex flex-col justify-center items-center w-full">
            <img src="/logo-white.png" alt="SkiFlow Rental" className="h-20 w-auto object-contain" />
          </div>
        </div>

        {/* Right — form */}
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
          <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
            <CardHeader className="space-y-1 text-center">
              <div className="flex justify-center mb-4 lg:hidden">
                <img src="/logo.png" alt="SkiFlow Rental" className="h-12 w-auto object-contain" />
              </div>
              <CardTitle className="text-2xl font-bold" style={{ fontFamily: "Plus Jakarta Sans" }}>
                Nueva contraseña
              </CardTitle>
              <CardDescription>Introduce tu nueva contraseña de acceso</CardDescription>
            </CardHeader>

            <CardContent>
              {done ? (
                <div className="text-center space-y-6 py-4">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
                  <div>
                    <p className="font-semibold text-lg">¡Contraseña actualizada!</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Ya puedes iniciar sesión con tu nueva contraseña.
                    </p>
                  </div>
                  <Button
                    className="w-full h-11 font-semibold bg-black hover:bg-black/90 text-white"
                    onClick={() => navigate("/login")}
                    data-testid="go-to-login-btn"
                  >
                    Ir al login
                  </Button>
                </div>
              ) : error && !loading ? (
                <div className="text-center space-y-6 py-4">
                  <XCircle className="h-16 w-16 text-red-500 mx-auto" />
                  <div>
                    <p className="font-semibold text-lg">Enlace inválido</p>
                    <p className="text-sm text-muted-foreground mt-1">{error}</p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full h-11"
                    onClick={() => navigate("/login")}
                    data-testid="request-new-link-btn"
                  >
                    Volver al login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Nueva contraseña</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPass ? "text" : "password"}
                        placeholder="Mínimo 6 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-11 pr-10"
                        data-testid="new-password-input"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass(!showPass)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirmar contraseña</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Repite la contraseña"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="h-11"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 font-semibold bg-black hover:bg-black/90 text-white"
                    disabled={loading}
                    data-testid="reset-password-submit-btn"
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Cambiar contraseña
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
